import React, { useState, useEffect } from 'react';
import { BarChart3, Users, CheckCircle, Clock, Star, Eye, Calendar } from 'lucide-react';
import ApiService from '../../services/api';

interface Survey {
  id: number;
  customerName: string;
  customerEmail: string;
  isCompleted: boolean;
  completedAt: string;
  createdAt: string;
  expiresAt: string;
  appointment: {
    id: number;
    date: string;
    time: string;
    serviceNames: string[];
  };
  responses?: Array<{
    id: number;
    rating?: number;
    answer?: string;
    question: {
      questionText: string;
    };
  }>;
}

interface SurveyStats {
  totalSurveys: number;
  completedSurveys: number;
  pendingSurveys: number;
  completionRate: string;
  ratingStats: Array<{
    questionId: number;
    averageRating: number;
    responseCount: number;
    question: {
      questionText: string;
    };
  }>;
}

const SurveyManagement: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [stats, setStats] = useState<SurveyStats | null>(null);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadSurveys();
    loadStats();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getSurveys();
      if (response.success) {
        setSurveys(response.surveys);
      }
    } catch (error) {
      console.error('Surveys load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await ApiService.getSurveyStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Stats load error:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleViewDetail = async (survey: Survey) => {
    try {
      const response = await ApiService.getSurveyDetail(survey.id);
      if (response.success) {
        setSelectedSurvey(response.survey);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Survey detail error:', error);
    }
  };

  const getStatusBadge = (survey: Survey) => {
    if (survey.isCompleted) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Tamamlandı
        </span>
      );
    } else if (new Date() > new Date(survey.expiresAt)) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Clock className="w-3 h-3 mr-1" />
          Süresi Doldu
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Bekliyor
        </span>
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Memnuniyet Anketleri</h1>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Anket</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSurveys}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tamamlanan</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedSurveys}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bekleyen</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingSurveys}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tamamlanma Oranı</p>
                <p className="text-2xl font-bold text-gray-900">%{stats.completionRate}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Stats */}
      {stats?.ratingStats && stats.ratingStats.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ortalama Puanlar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.ratingStats.map((stat) => (
              <div key={stat.questionId} className="border rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {stat.question.questionText}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                    <span className="text-lg font-bold text-gray-900">
                      {stat.averageRating.toFixed(1)}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">/5</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {stat.responseCount} cevap
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Surveys Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Anket Listesi</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Müşteri
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Randevu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Oluşturulma
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {surveys.map((survey) => (
                <tr key={survey.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {survey.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {survey.customerEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">
                        {formatDate(survey.appointment.date)} {survey.appointment.time}
                      </div>
                      <div className="text-sm text-gray-500">
                        {Array.isArray(survey.appointment.serviceNames) 
                          ? survey.appointment.serviceNames.join(', ')
                          : survey.appointment.serviceNames || 'Hizmet bilgisi yok'
                        }
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(survey)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(survey.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetail(survey)}
                      className="text-blue-600 hover:text-blue-900 flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Detay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedSurvey && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Anket Detayları
                </h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Müşteri Bilgileri</h4>
                  <p className="text-sm text-gray-600">
                    {selectedSurvey.customerName} - {selectedSurvey.customerEmail}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Randevu Bilgileri</h4>
                  <p className="text-sm text-gray-600">
                    {formatDate(selectedSurvey.appointment.date)} {selectedSurvey.appointment.time}
                  </p>
                  <p className="text-sm text-gray-600">
                    {Array.isArray(selectedSurvey.appointment.serviceNames) 
                      ? selectedSurvey.appointment.serviceNames.join(', ')
                      : selectedSurvey.appointment.serviceNames || 'Hizmet bilgisi yok'
                    }
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900">Anket Durumu</h4>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(selectedSurvey)}
                    <span className="text-sm text-gray-500">
                      Oluşturulma: {formatDate(selectedSurvey.createdAt)}
                    </span>
                  </div>
                </div>

                {selectedSurvey.isCompleted && (
                  <div>
                    <h4 className="font-medium text-gray-900">Tamamlanma</h4>
                    <p className="text-sm text-gray-600">
                      {formatDate(selectedSurvey.completedAt)}
                    </p>
                  </div>
                )}

                {selectedSurvey.responses && selectedSurvey.responses.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900">Cevaplar</h4>
                    <div className="space-y-2">
                      {selectedSurvey.responses.map((response: any) => (
                        <div key={response.id} className="border rounded p-3">
                          <p className="font-medium text-sm text-gray-700">
                            {response.question.questionText}
                          </p>
                          {response.rating && (
                            <div className="flex items-center mt-1">
                              <Star className="w-4 h-4 text-yellow-400 mr-1" />
                              <span className="text-sm text-gray-600">
                                {response.rating}/5
                              </span>
                            </div>
                          )}
                          {response.answer && (
                            <p className="text-sm text-gray-600 mt-1">
                              {response.answer}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyManagement;
